import moment from 'moment-mini';

export const modelKey = row => row.entryType === 'voice' ? 'Voice Mode' : (row.modelName || row.model || 'Other usage');
export const formatUsageNumber = value => {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return '—';
  const number = Number(value);
  const whole = Math.round(number);
  if (whole === 0 && number > 0) return '<1';
  return whole.toLocaleString();
};
export const formatCreditChange = value => {
  if (!Number.isFinite(Number(value)) || value == null) return '—';
  const n = Number(value);
  const whole = Math.round(n);
  if (whole === 0 && n !== 0) return `${n < 0 ? '−' : '+'}<1`;
  return `${n < 0 ? '−' : n > 0 ? '+' : ''}${Math.abs(whole).toLocaleString()}`;
};

// All views use the same local calendar boundaries. Positive grants/purchases
// never offset spending or enter the model-share denominator.
export function buildUsageInsights(rows, days, now = new Date()) {
  const end = moment(now).endOf('day');
  const start = moment(now).startOf('day').subtract(days - 1, 'days');
  const daily = new Map();
  for (let i = 0; i < days; i++) daily.set(start.clone().add(i, 'days').format('YYYY-MM-DD'), 0);
  const models = new Map();
  let spent = 0;
  let added = 0;
  let charges = 0;
  const filtered = rows.filter(row => {
    const date = moment(row.date);
    return row.date && date.isValid() && date.valueOf() >= start.valueOf() && date.valueOf() <= end.valueOf();
  });
  for (const row of filtered) {
    const value = Number(row.credits);
    if (!Number.isFinite(value)) continue;
    if (value > 0) added += value;
    if (value >= 0) continue;
    const cost = -value;
    spent += cost;
    charges++;
    const date = moment(row.date).format('YYYY-MM-DD');
    daily.set(date, daily.get(date) + cost);
    const key = modelKey(row);
    const model = models.get(key) || { name: key, credits: 0, count: 0, voiceCredits: 0 };
    model.credits += cost;
    model.count++;
    if (row.isFromVoice || row.memo === 'Via Voice Mode') model.voiceCredits += cost;
    models.set(key, model);
  }
  return {
    rows: filtered.sort((a, b) => new Date(b.date) - new Date(a.date)), spent, added, charges,
    models: [...models.values()].sort((a, b) => b.credits - a.credits).map(model => ({ ...model, share: spent ? model.credits / spent * 100 : 0, average: model.credits / model.count })),
    daily: [...daily].map(([date, value]) => ({ date, value })),
  };
}

export async function loadUsageWindow(fetchPage, { startDate, endDate, isCancelled = () => false, onProgress = () => {} }) {
  // Paint recent activity after a small first request. Analytics never blocks it.
  const recent = await fetchPage(1, { startDate, endDate, pageSize: 20 });
  if (isCancelled()) return null;
  onProgress((recent.creditUsages || []).length, recent.creditUsages || []);
  if (!recent.pagination?.hasMore) return recent.creditUsages || [];

  // New servers return the entire bounded window in one lightweight query.
  // Pagination remains a compatibility fallback during rolling deployments.
  const rows = [];
  for (let page = 1; !isCancelled(); page++) {
    const result = await fetchPage(page, { startDate, endDate, pageSize: 200, overview: true });
    if (isCancelled()) return null;
    const batch = result.creditUsages || [];
    rows.push(...batch);
    onProgress(rows.length, rows.slice());
    if (!result.pagination?.hasMore) return rows;
    // Never label an incomplete history as a complete spending total.
    if (!batch.length) throw new Error('Incomplete usage history');
  }
  return null;
}

export function buildChatBreakdown(rows) {
  const chats = new Map();
  for (const row of rows) {
    const credits = Number(row.credits);
    if (row.activityType !== 'chat' || !row.conversationId || !Number.isFinite(credits) || credits >= 0) continue;
    const chat = chats.get(row.conversationId) || { id: row.conversationId, title: row.conversationTitle, credits: 0, count: 0, models: new Set(), link: `/c/${row.conversationId}` };
    chat.credits -= credits;
    chat.count++;
    chat.models.add(modelKey(row));
    chats.set(chat.id, chat);
  }
  return [...chats.values()].sort((a, b) => b.credits - a.credits).map(chat => ({ ...chat, models: [...chat.models] }));
}

// Collapse Work calls before display, while keeping the original rows for
// accounting and filters. Group keys use IDs, never titles or time proximity.
export function groupUsageHistory(rows) {
  const result = [];
  const grouped = new Map();
  for (const row of rows) {
    const isWork = row.activityType === 'work';
    const taskKey = row.workTaskId || row.conversationId;
    const key = isWork && taskKey ? `work:${taskKey}` : row.activityType === 'api' ? 'unlinked-api' : null;
    if (!key || Number(row.credits) > 0) { result.push(row); continue; }
    let group = grouped.get(key);
    if (!group) {
      group = { ...row, groupKey: key, isUsageGroup: true, isUnlinkedApi: key === 'unlinked-api', credits: 0, charges: [], title: row.workTaskTitle || row.conversationTitle, link: row.workTaskLink || (row.conversationId && isWork ? `/c/${row.conversationId}` : null) };
      grouped.set(key, group);
      result.push(group);
    }
    group.credits += Number(row.credits) || 0;
    group.charges.push(row);
    if (new Date(row.date) > new Date(group.date)) group.date = row.date;
  }
  return result.sort((a, b) => new Date(b.date) - new Date(a.date));
}
export function groupedModelCosts(rows) {
  const models = new Map();
  for (const row of rows) {
    if (!(Number(row.credits) < 0)) continue;
    const name = modelKey(row);
    const item = models.get(name) || { name, credits: 0, count: 0 };
    item.credits -= Number(row.credits);
    item.count++;
    models.set(name, item);
  }
  return [...models.values()].sort((a, b) => b.credits - a.credits);
}
